import { useSelector } from 'react-redux'
import { gql, useQuery } from '@apollo/client'
import {
  useMemo, useEffect, useState, useRef
} from 'react'

function useWeeklySearchStatus() {
  const locationCode = useSelector((state) => state.site.primaryAssigned.locationCode)
  const primarySiteCode = useSelector((state) => state.site.primaryAssigned.code)
  const primarySiteType = useSelector((state) => state.site.primaryAssigned.type)
  const selectedSiteCode = useSelector((state) => state.site.selected.code)
  const selectedSiteType = useSelector((state) => state.site.selected.type)
  const siteCode = selectedSiteCode == null ? primarySiteCode : selectedSiteCode
  const siteType = selectedSiteCode == null ? primarySiteType : selectedSiteType

  // Wait for critical dependencies to be loaded
  const userLocationCode = useSelector((state) => state.user.locationCode)
  const userFullName = useSelector((state) => state.user.fullName)
  const userAzureId = useSelector((state) => state.user.azureId)
  const permissionsLoaded = useSelector((state) => state.permissions.isForbidden !== null)
  const isForbidden = useSelector((state) => state.permissions.isForbidden)
  const hasMultipleLocations = useSelector((state) => state.permissions.hasMultipleLocations)

  // Enhanced dependency check to ensure ALL authentication and permissions are loaded
  const authenticationComplete = Boolean(userAzureId && userFullName)
  const siteDataComplete = Boolean(locationCode && siteCode && siteType)
  const permissionsComplete = Boolean(
    permissionsLoaded
    && userLocationCode
    && hasMultipleLocations !== null
  )

  // Memoize the shouldExecuteQuery to prevent unnecessary re-renders
  const shouldExecuteQuery = useMemo(() => {
    // Core dependencies that must be loaded
    const coreReady = Boolean(
      authenticationComplete
      && siteDataComplete
      && permissionsComplete
      && !isForbidden
    )

    return coreReady
  }, [
    authenticationComplete,
    siteDataComplete,
    permissionsComplete,
    isForbidden
  ])

  // Use a ref to track if we've already started the query to prevent re-triggers
  const queryStartedRef = useRef(false)
  const [shouldStartQuery, setShouldStartQuery] = useState(false)

  useEffect(() => {
    if (shouldExecuteQuery && !queryStartedRef.current) {
      queryStartedRef.current = true
      setShouldStartQuery(true)
    }
  }, [shouldExecuteQuery])

  // Add a delay mechanism to ensure all other API calls complete first
  const [delayedShouldExecute, setDelayedShouldExecute] = useState(false)
  
  useEffect(() => {
    if (shouldStartQuery) {
      // Wait 500ms after all dependencies are ready to ensure other API calls complete
      const timer = setTimeout(() => {
        setDelayedShouldExecute(true)
      }, 500)
      
      return () => clearTimeout(timer)
    }
    
    setDelayedShouldExecute(false)
    return undefined
  }, [shouldStartQuery])

  // Memoize the query variables to prevent unnecessary re-renders
  const queryVariables = useMemo(() => ({
    siteLocationCode: locationCode,
    siteCode,
    siteType
  }), [locationCode, siteCode, siteType])

  const GET_WEEKLY_SEARCH_STATUS_FOR_BRANDS = gql`
    query GetWeeklySearchStatusForBrands(
      $siteLocationCode: String!,
      $siteCode: String!,
      $siteType: String!
    ) {
      getWeeklySearchStatusForBrands(
        siteLocationCode: $siteLocationCode,
        siteCode: $siteCode,
        siteType: $siteType
      ) {
        sunday
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        weekStartDate
        brand
      }
    }
  `

  const {
    loading,
    error,
    data,
    refetch
  } = useQuery(GET_WEEKLY_SEARCH_STATUS_FOR_BRANDS, {
    variables: queryVariables,
    skip: !delayedShouldExecute,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    context: {
      timeout: 60000,
      retryAttempts: 3,
      retryDelay: 2000
    },
    pollInterval: 0,
    partialRefetch: true
  })

  const emptyStatus = {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    weekStartDate: new Date()
  }

  const statusList = data?.getWeeklySearchStatusForBrands || []
  const sainsburysStatus = statusList.find((status) => status.brand === 'Sainsburys') || emptyStatus
  const argosStatus = statusList.find((status) => status.brand === 'Argos') || emptyStatus

  // Show loading when we should execute the query and it's actually loading or waiting
  const isLoadingStatus = (shouldStartQuery && !delayedShouldExecute) || (loading && delayedShouldExecute)

  return {
    loading: isLoadingStatus,
    error,
    sainsburysStatus,
    argosStatus,
    refetch
  }
}

export default useWeeklySearchStatus
